import React, { useState, useCallback, useEffect } from 'react';
import WarningIcon from '@moda/icons/warning-16';
import { Stack, ControlLink, Text } from '@moda/om';
import { loadAmazonWidgetScript } from '../../../lib/loadScript';
import { reportError } from '../../../lib/reportError';
import { isLoggedInOnAmazon, logoutOnAmazon, waitForAmazonWidgetScript } from '../../../lib/amazon';
import { useErrorModal } from '../../../hooks/useErrorModal';
import { useSocialAccountConnection } from '../../../hooks/useSocialAccountConnection';
import { SocialAuthProvider } from '../../../hooks/useSocialAuth';
import { AmazonAddressWidget } from '../AmazonAddressWidget';
import { AmazonWalletWidget } from '../AmazonWalletWidget';
import { AmazonConsentWidget } from '../AmazonConsentWidget';
import { Loading } from '../../Loading';
import './AmazonShippingAndPaymentWidgets.scss';

type Props = {
  onReady?: (billingAgreementId: string) => void;
  onConsentStatusChange?: (checked: boolean) => void;
};

enum Mode {
  Resting,
  Loading,
  SessionExpired
}

export const AmazonShippingAndPaymentWidgets: React.FC<Props> = ({
  onReady,
  onConsentStatusChange
}) => {
  const { showErrorModal } = useErrorModal();
  const { handleConnect } = useSocialAccountConnection(SocialAuthProvider.Amazon);
  const [mode, setMode] = useState(Mode.Loading);
  const [billingAgreementId, setBillingAgreementId] = useState('');

  const handleAddressWidgetReady = useCallback(
    (billingAgreementId: string) => {
      setBillingAgreementId(billingAgreementId);
      onReady?.(billingAgreementId);
    },
    [setBillingAgreementId, onReady]
  );

  const handleAddressWidgetError = useCallback(() => {
    logoutOnAmazon();
    setMode(Mode.SessionExpired);
  }, [setMode]);

  const handleLogin = useCallback(async () => {
    try {
      await handleConnect();
      setMode(Mode.Resting);
    } catch (error) {
      reportError('AMAZON_PAY_LOGIN', error);
      showErrorModal();
    }
  }, [handleConnect, showErrorModal]);

  useEffect(() => {
    const checkAmazonSession = async () => {
      try {
        await waitForAmazonWidgetScript();
        const isLoggedIn = await isLoggedInOnAmazon();
        setMode(isLoggedIn ? Mode.Resting : Mode.SessionExpired);
      } catch (error) {
        reportError('AMAZON_PAY_CHECK_SESSION', error);
        showErrorModal();
      }
    };

    if (!window.OffAmazonPayments) loadAmazonWidgetScript();
    if (mode === Mode.Loading) checkAmazonSession();
  }, [showErrorModal, mode, setMode]);

  return (
    <Stack space={4} className="AmazonShippingAndPaymentWidgets">
      <div className="AmazonShippingAndPaymentWidgets__top">
        {mode === Mode.Loading && <Loading />}
        {mode === Mode.SessionExpired && (
          <Stack direction="horizontal" space={1} alignItems="center">
            <Text color="code-red">
              <WarningIcon />
            </Text>
            <Text color="code-red">
              Your Amazon session has expired.{' '}
              <Text color="ink">
                <ControlLink onClick={handleLogin}>Log in with Amazon</ControlLink>
              </Text>{' '}
              in order to continue.
            </Text>
          </Stack>
        )}
        <div className="AmazonShippingAndPaymentWidgets__widget">
          {mode === Mode.Resting && (
            <AmazonAddressWidget
              onReady={handleAddressWidgetReady}
              onError={handleAddressWidgetError}
            />
          )}
        </div>
        <div className="AmazonShippingAndPaymentWidgets__widget">
          {billingAgreementId && <AmazonWalletWidget billingAgreementId={billingAgreementId} />}
        </div>
      </div>
      {billingAgreementId && (
        <AmazonConsentWidget
          billingAgreementId={billingAgreementId}
          onChange={onConsentStatusChange}
        />
      )}
    </Stack>
  );
};
